<template>
  <div class="vp-timeline-wrap">
    <div class="vp-timeline-row">
      <span class="vp-time vp-time--muted">{{
        visibleTimeMarks.length
          ? formatSegmentTime(
              new Date(
                visibleTimeMarks[0].timestamp ||
                  visibleTimeMarks[0].endTimestamp
              )
            )
          : ""
      }}</span>
      <div
        class="vp-track-area"
        ref="timelineBar"
        @click="!isCutModeEnabled && $emit('seek', $event)"
        @mousemove="$emit('show-tooltip', $event)"
        @mouseleave="$emit('hide-tooltip')"
        @mousedown.prevent="!isCutModeEnabled && $emit('start-drag', $event)"
      >
        <div class="vp-event-labels">
          <div
            v-for="evt in eventSegments"
            :key="'lbl-' + evt.id"
            class="vp-event-label"
            :style="{
              left:
                parseFloat(evt.leftPct) + parseFloat(evt.widthPct) / 2 + '%',
            }"
          >
            {{ evt.elementId }}
          </div>
        </div>
        <div class="vp-track">
          <div class="vp-track-segments">
            <div
              v-for="(seg, i) in segments"
              :key="'seg-' + i"
              class="vp-track-segment"
              :class="{
                'vp-track-segment--video': seg.type === 'video' || !seg.type,
                'vp-track-segment--separator': seg.type === 'separator',
              }"
              :style="{ width: getSegmentWidthPercentage(seg) + '%' }"
            />
          </div>
          <div
            class="vp-track-progress"
            :style="{ width: (playerCurrentTime / totalSeconds) * 100 + '%' }"
          />
          <template v-if="timeMarksData && timeMarksData.length > 0">
            <template v-for="(mark, i) in timeMarksData">
              <div
                v-if="isIntermediateMark(i)"
                :key="'mark-' + i"
                class="vp-track-divider"
                :style="{ left: getTimeMarkPosition(i) + '%' }"
              />
            </template>
          </template>
          <div
            v-for="evt in eventSegments"
            :key="'evt-' + evt.id"
            class="vp-event-marker"
            :class="{ 'vp-event-marker--active': activeEventId === evt.id }"
            :style="{
              left: evt.leftPct + '%',
              width: evt.widthPct + '%',
              backgroundColor: evt.color,
            }"
            :title="evt.elementId"
          />
          <!-- Marcadores de eventos detectados por IA (context-events) -->
          <template
            v-if="!sectionCollapsed.contextEvents && contextEvents.length > 0"
          >
            <div
              v-for="evt in contextEventSegments"
              :key="'ctx-evt-' + evt.id"
              class="vp-event-marker vp-event-marker--ai"
              :class="{ 'vp-event-marker--ai-unload': evt.type === 'UNLOAD' }"
              :style="{ left: evt.leftPct + '%', width: evt.widthPct + '%' }"
              :title="
                (evt.type === 'LOAD' ? 'Carga' : 'Descarga') +
                ' (detectado por IA)'
              "
            />
          </template>
          <div
            v-if="isCutModeEnabled"
            class="vp-cut-range"
            :style="{
              left: (cutStart / totalSeconds) * 100 + '%',
              width: ((cutEnd - cutStart) / totalSeconds) * 100 + '%',
            }"
          />
          <div
            v-if="isCutModeEnabled"
            class="vp-cut-handle vp-cut-handle--start"
            :style="{ left: (cutStart / totalSeconds) * 100 + '%' }"
            @mousedown.stop.prevent="$emit('start-handle-drag', 'start')"
          />
          <div
            v-if="isCutModeEnabled"
            class="vp-cut-handle vp-cut-handle--end"
            :style="{ left: (cutEnd / totalSeconds) * 100 + '%' }"
            @mousedown.stop.prevent="$emit('start-handle-drag', 'end')"
          />
          <div
            class="vp-thumb"
            :style="{ left: (playerCurrentTime / totalSeconds) * 100 + '%' }"
          />
        </div>
        <div
          v-if="tooltipVisible && !isGroupMaster"
          class="vp-tooltip"
          :style="{ left: tooltipX + 'px' }"
        >
          {{ tooltipTime }}
        </div>
        <div class="vp-time-labels">
          <span
            v-for="(time, i) in visibleTimeMarks"
            :key="'tl-' + i"
            class="vp-time-label"
            :class="{ 'vp-time-label--separator': time.isSeparator }"
            :style="{ left: time.position + '%', transform: time.transform }"
          >
            <span v-if="time.isSeparator"
              >{{ formatSegmentTime(new Date(time.endTimestamp))
              }}<span style="margin: 0 3px; color: #94a3b8">→</span
              >{{ formatSegmentTime(new Date(time.startTimestamp)) }}</span
            >
            <span v-else>{{
              formatSegmentTime(new Date(time.timestamp))
            }}</span>
          </span>
        </div>
      </div>
      <span class="vp-time vp-time--muted">{{
        visibleTimeMarks.length
          ? formatSegmentTime(
              new Date(
                visibleTimeMarks[visibleTimeMarks.length - 1].timestamp ||
                  visibleTimeMarks[visibleTimeMarks.length - 1].startTimestamp
              )
            )
          : ""
      }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: "VideoTimeline",
  props: {
    visibleTimeMarks: { type: Array, required: true },
    segments: { type: Array, required: true },
    eventSegments: { type: Array, required: true },
    contextEventSegments: { type: Array, required: true },
    contextEvents: { type: Array, required: true },
    timeMarksData: { type: Array, default: null },
    sectionCollapsed: { type: Object, required: true },
    playerCurrentTime: { type: Number, required: true },
    totalSeconds: { type: Number, required: true },
    isCutModeEnabled: { type: Boolean, required: true },
    cutStart: { type: Number, required: true },
    cutEnd: { type: Number, required: true },
    tooltipVisible: { type: Boolean, required: true },
    tooltipX: { type: Number, required: true },
    tooltipTime: { type: String, required: true },
    isGroupMaster: { type: Boolean, required: true },
    activeEventId: { type: [Number, String], default: null },
  },
  methods: {
    getSegmentWidthPercentage(segment) {
      if (segment.type) {
        const segmentDuration =
          (new Date(segment.endTime) - new Date(segment.startTime)) / 1000;
        return (segmentDuration / this.totalSeconds) * 100;
      }
      return 100 / this.segments.length;
    },
    getTimeMarkPosition(index) {
      if (this.timeMarksData && this.timeMarksData.length > 0) {
        const mark = this.timeMarksData[index];
        return (mark.videoOffset / this.totalSeconds) * 100;
      }
      return index * (100 / (this.timeMarksData.length - 1));
    },
    isIntermediateMark(index) {
      if (!this.timeMarksData || this.timeMarksData.length === 0) return false;
      const mark = this.timeMarksData[index];
      const markOffset = mark.videoOffset;
      for (const seg of this.segments || []) {
        if (seg.type === "video") {
          const segStart = seg.videoOffset;
          const segEnd = seg.videoOffset + seg.duration;
          if (
            Math.abs(markOffset - segStart) < 0.1 ||
            Math.abs(markOffset - segEnd) < 0.1
          )
            return false;
        }
      }
      return true;
    },
    formatSegmentTime(dateTime) {
      const d = new Date(dateTime);
      return d.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
  },
};
</script>

<style>
.vp-timeline-wrap {
  position: relative;
  margin-bottom: 10px;
}
.vp-timeline-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 4px;
  margin-bottom: 4px;
}
.vp-time {
  font-family: monospace;
  font-size: 11px;
  white-space: nowrap;
  flex-shrink: 0;
}
.vp-time--muted {
  color: var(--vp-muted);
}
.vp-track-area {
  flex: 1;
  position: relative;
  cursor: pointer;
  padding-top: 22px;
  padding-bottom: 18px;
}
.vp-event-labels {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 18px;
  pointer-events: none;
}
.vp-track {
  position: relative;
  width: 100%;
  height: 5px;
  border-radius: 3px;
  background: var(--vp-border);
  box-sizing: border-box;
}
.vp-track-segments {
  position: absolute;
  inset: 0;
  display: flex;
  border-radius: 3px;
  overflow: hidden;
}
.vp-track-segment {
  height: 100%;
  flex-shrink: 0;
  flex-grow: 0;
}
.vp-track-segment--video {
  background: #cbd5e1;
}
.vp-track-segment--separator {
  background: #0f172a;
}
.vp-track-progress {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  max-width: 100%;
  border-radius: 3px;
  background: var(--vp-accent);
  pointer-events: none;
  transition: width 0.08s linear;
  z-index: 2;
}
.vp-track-divider {
  position: absolute;
  top: 0;
  width: 2px;
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  pointer-events: none;
  z-index: 3;
}
.vp-event-marker {
  position: absolute;
  top: -2px;
  height: 9px;
  border-radius: 1px;
  pointer-events: auto;
  z-index: 5;
  opacity: 0.8;
  transition: opacity 0.2s ease, filter 0.2s ease;
}
.vp-event-marker:hover {
  opacity: 1;
  filter: brightness(0.9);
}
.vp-event-marker--active {
  opacity: 1;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
}
.vp-event-marker--ai {
  top: -6px;
  height: 5px;
  background: var(--vp-ai);
  opacity: 0.55;
  border: 1px solid #fff;
}
.vp-event-marker--ai-unload {
  background: #a78bfa;
}
.vp-event-label {
  position: absolute;
  transform: translateX(-50%);
  font-size: 9px;
  font-weight: 700;
  color: #16a34a;
  white-space: nowrap;
  letter-spacing: 0.02em;
  line-height: 1;
  pointer-events: none;
}
.vp-thumb {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: #fff;
  pointer-events: none;
  box-shadow: 0 0 0 3px var(--vp-accent), 0 2px 6px rgba(0, 0, 0, 0.18);
  z-index: 6;
  transition: left 0.08s linear;
}
.vp-cut-range {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 7px;
  background-color: rgba(251, 191, 36, 0.6);
  border-radius: 3px;
  z-index: 4;
  pointer-events: none;
}
.vp-cut-handle {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 10px;
  height: 20px;
  background: #f59e0b;
  border-radius: 3px;
  cursor: ew-resize;
  z-index: 7;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
}
.vp-tooltip {
  position: absolute;
  top: calc(100% - 14px);
  background: rgba(15, 23, 42, 0.8);
  color: #fff;
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 0.72rem;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
  transform: translateX(-50%);
}
.vp-time-labels {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 16px;
  pointer-events: none;
}
.vp-time-label {
  position: absolute;
  font-size: 0.7rem;
  color: var(--vp-muted);
  white-space: nowrap;
}
</style>
